import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { AdminShell } from '../components/admin/AdminShell';
import { adminApi } from '../services/adminApi';
import { Project, PaginatedResponse } from '../types/admin';
import {
  PageHeader,
  PageTitle,
  PrimaryButton,
  SecondaryButton,
  Table,
  Th,
  Td,
  Tr,
  StatusBadge,
  IconButton,
  ActionButtons,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalTitle,
  ModalBody,
  ModalFooter,
  CloseButton,
  Form,
  FormGroup,
  FormRow,
  Label,
  Input,
  Textarea,
  AlertMessage,
  EmptyStateContainer,
  EmptyStateIcon,
  EmptyStateTitle,
  EmptyStateText,
  LoadingSpinner,
  Tag,
  TagList,
  InfoGrid,
  InfoItem,
  InfoLabel,
  InfoValue,
  MonoText,
} from '../styles/adminShared';

// Page-specific styled components
const TagsContainer = styled(TagList)`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

interface FormData {
  name: string;
  description: string;
  logo: string;
  internalId: string;
  dataCategories: string;
}

const initialFormData: FormData = {
  name: '',
  description: '',
  logo: '',
  internalId: '',
  dataCategories: '',
};

export const Projects: React.FC = () => {
  const [projects, setProjects] = useState<PaginatedResponse<Project> | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewingProject, setViewingProject] = useState<Project | null>(null);
  const [editingProject, setEditingProject] = useState<Project | null>(null);
  const [formData, setFormData] = useState<FormData>(initialFormData);

  useEffect(() => {
    loadProjects();
  }, []);

  const loadProjects = async () => {
    try {
      setLoading(true);
      const data = await adminApi.listProjects(1, 25);
      setProjects(data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load projects');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingProject(null);
    setFormData(initialFormData);
    setShowModal(true);
  };

  const handleView = async (project: Project) => {
    try {
      const fullData = await adminApi.getProject(project.projectId);
      setViewingProject(fullData);
      setShowViewModal(true);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load project details');
    }
  };

  const handleEdit = (project: Project) => {
    setEditingProject(project);
    setFormData({
      name: project.name || '',
      description: project.description || '',
      logo: project.logo || '',
      internalId: project.internalId || '',
      dataCategories: project.dataCategories?.join(', ') || '',
    });
    setShowModal(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    try {
      const projectData: Partial<Project> = {
        name: formData.name,
        description: formData.description,
        logo: formData.logo || undefined,
        internalId: formData.internalId || undefined,
        dataCategories: formData.dataCategories
          ? formData.dataCategories.split(',').map(c => c.trim()).filter(Boolean)
          : [],
      };

      if (editingProject) {
        await adminApi.updateProject(editingProject.projectId, projectData);
        setSuccess('Project updated successfully');
      } else {
        await adminApi.createProject(projectData);
        setSuccess('Project created successfully');
      }

      setShowModal(false);
      loadProjects();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save project');
    }
  };

  const handleToggleStatus = async (project: Project) => {
    try {
      if (project.status === 'ACTIVE') {
        await adminApi.disableProject(project.projectId);
      } else {
        await adminApi.enableProject(project.projectId);
      }
      loadProjects();
      setSuccess(`Project ${project.status === 'ACTIVE' ? 'disabled' : 'enabled'} successfully`);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update project status');
    }
  };

  const projectList = projects?.data || projects?.content || [];

  return (
    <AdminShell>
      <PageHeader>
        <PageTitle>Projects</PageTitle>
        <PrimaryButton onClick={handleAdd}>+ Add Project</PrimaryButton>
      </PageHeader>

      {error && <AlertMessage $variant="error">{error}</AlertMessage>}
      {success && <AlertMessage $variant="success">{success}</AlertMessage>}

      {loading ? (
        <EmptyStateContainer>
          <LoadingSpinner>Loading...</LoadingSpinner>
        </EmptyStateContainer>
      ) : projectList.length === 0 ? (
        <EmptyStateContainer>
          <EmptyStateIcon>📁</EmptyStateIcon>
          <EmptyStateTitle>No projects found</EmptyStateTitle>
          <EmptyStateText>Create your first project to get started.</EmptyStateText>
          <PrimaryButton onClick={handleAdd}>Create Project</PrimaryButton>
        </EmptyStateContainer>
      ) : (
        <Table>
          <thead>
            <tr>
              <Th>Name</Th>
              <Th>Internal ID</Th>
              <Th>Data Categories</Th>
              <Th>Status</Th>
              <Th>Templates</Th>
              <Th>Actions</Th>
            </tr>
          </thead>
          <tbody>
            {projectList.map((project) => (
              <Tr key={project.projectId}>
                <Td>
                  <strong>{project.name}</strong>
                  {project.description && (
                    <div style={{ fontSize: '12px', marginTop: '4px' }}>
                      <InfoLabel>{project.description.substring(0, 100)}...</InfoLabel>
                    </div>
                  )}
                </Td>
                <Td>
                  <MonoText>{project.internalId || '-'}</MonoText>
                </Td>
                <Td>
                  <TagsContainer>
                    {(project.dataCategories || []).slice(0, 3).map((cat, i) => (
                      <Tag key={i}>{cat}</Tag>
                    ))}
                    {(project.dataCategories || []).length > 3 && (
                      <Tag>+{project.dataCategories!.length - 3}</Tag>
                    )}
                  </TagsContainer>
                </Td>
                <Td>
                  <StatusBadge $status={project.status || 'ACTIVE'}>
                    {project.status || 'ACTIVE'}
                  </StatusBadge>
                </Td>
                <Td>{project.linkedTemplatesCount || 0}</Td>
                <Td>
                  <ActionButtons>
                    <IconButton $variant="view" onClick={() => handleView(project)}>
                      👁️ View
                    </IconButton>
                    <IconButton $variant="edit" onClick={() => handleEdit(project)}>
                      ✏️ Edit
                    </IconButton>
                    <IconButton
                      $variant={project.status === 'ACTIVE' ? 'danger' : 'success'}
                      onClick={() => handleToggleStatus(project)}
                    >
                      {project.status === 'ACTIVE' ? '⏸️ Disable' : '▶️ Enable'}
                    </IconButton>
                  </ActionButtons>
                </Td>
              </Tr>
            ))}
          </tbody>
        </Table>
      )}

      {/* View Project Modal */}
      <ModalOverlay $show={showViewModal && viewingProject !== null}>
        {showViewModal && viewingProject && (
          <ModalContent>
            <ModalHeader>
              <ModalTitle>📁 {viewingProject.name}</ModalTitle>
              <CloseButton onClick={() => setShowViewModal(false)}>✕</CloseButton>
            </ModalHeader>
            
            <ModalBody>
              <InfoGrid>
                <InfoItem>
                  <InfoLabel>Project ID</InfoLabel>
                  <MonoText>{viewingProject.projectId}</MonoText>
                </InfoItem>
                <InfoItem>
                  <InfoLabel>Internal ID</InfoLabel>
                  <InfoValue>{viewingProject.internalId || 'N/A'}</InfoValue>
                </InfoItem>
                <InfoItem>
                  <InfoLabel>Status</InfoLabel>
                  <StatusBadge $status={viewingProject.status || 'ACTIVE'}>
                    {viewingProject.status || 'ACTIVE'}
                  </StatusBadge>
                </InfoItem>
                <InfoItem>
                  <InfoLabel>Linked Templates</InfoLabel>
                  <InfoValue>{viewingProject.linkedTemplatesCount || 0}</InfoValue>
                </InfoItem>
                {viewingProject.description && (
                  <InfoItem>
                    <InfoLabel>Description</InfoLabel>
                    <InfoValue>{viewingProject.description}</InfoValue>
                  </InfoItem>
                )}
                {viewingProject.dataCategories && viewingProject.dataCategories.length > 0 && (
                  <InfoItem>
                    <InfoLabel>Data Categories</InfoLabel>
                    <TagsContainer>
                      {viewingProject.dataCategories.map((cat, i) => (
                        <Tag key={i}>{cat}</Tag>
                      ))}
                    </TagsContainer>
                  </InfoItem>
                )}
                <InfoItem>
                  <InfoLabel>Created At</InfoLabel>
                  <InfoValue>
                    {viewingProject.createdAt ? new Date(viewingProject.createdAt).toLocaleString() : 'N/A'}
                  </InfoValue>
                </InfoItem>
                <InfoItem>
                  <InfoLabel>Updated At</InfoLabel>
                  <InfoValue>
                    {viewingProject.updatedAt ? new Date(viewingProject.updatedAt).toLocaleString() : 'N/A'}
                  </InfoValue>
                </InfoItem>
                {viewingProject.logo && (
                  <InfoItem>
                    <InfoLabel>Logo</InfoLabel>
                    <img 
                      src={viewingProject.logo} 
                      alt="Project Logo" 
                      style={{ maxWidth: '120px', maxHeight: '80px', borderRadius: '8px' }}
                      onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                    />
                  </InfoItem>
                )}
              </InfoGrid>
            </ModalBody>
            
            <ModalFooter>
              <SecondaryButton onClick={() => setShowViewModal(false)}>Close</SecondaryButton>
              <PrimaryButton onClick={() => { setShowViewModal(false); handleEdit(viewingProject); }}>Edit Project</PrimaryButton>
            </ModalFooter>
          </ModalContent>
        )}
      </ModalOverlay>

      <ModalOverlay $show={showModal}>
        <ModalContent>
          <ModalHeader>
            <ModalTitle>{editingProject ? 'Edit Project' : 'Add New Project'}</ModalTitle>
            <CloseButton onClick={() => setShowModal(false)}>✕</CloseButton>
          </ModalHeader>
          
          <ModalBody>
            <Form id="project-form" onSubmit={handleSubmit}>
              <FormRow>
                <FormGroup>
                  <Label>Project Name *</Label>
                  <Input
                    type="text"
                    value={formData.name}
                    onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => setFormData({ ...formData, name: e.target.value })}
                    required
                    placeholder="e.g., Patient Registration System"
                  />
                </FormGroup>
                <FormGroup>
                  <Label>Internal ID</Label>
                  <Input
                    type="text"
                    value={formData.internalId}
                    onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => setFormData({ ...formData, internalId: e.target.value })}
                    placeholder="e.g., PROJ-001"
                  />
                </FormGroup>
              </FormRow>

              <FormGroup>
                <Label>Description</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Describe the project and its purpose..."
                />
              </FormGroup>

              <FormGroup>
                <Label>Logo URL</Label>
                <Input
                  type="url"
                  value={formData.logo}
                  onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => setFormData({ ...formData, logo: e.target.value })}
                  placeholder="https://example.com/logo.png"
                />
              </FormGroup>

              <FormGroup>
                <Label>Data Categories (comma-separated)</Label>
                <Input
                  type="text"
                  value={formData.dataCategories}
                  onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => setFormData({ ...formData, dataCategories: e.target.value })}
                  placeholder="e.g., Personal Information, Medical History, Contact Details"
                />
              </FormGroup>
            </Form>
          </ModalBody>
          
          <ModalFooter>
            <SecondaryButton type="button" onClick={() => setShowModal(false)}>
              Cancel
            </SecondaryButton>
            <PrimaryButton type="submit" form="project-form">
              {editingProject ? 'Update' : 'Create'} Project
            </PrimaryButton>
          </ModalFooter>
        </ModalContent>
      </ModalOverlay>
    </AdminShell>
  );
};

export default Projects;
